<template>
  <v-slide-x-reverse-transition>
    <v-card raised class="elevation-5 ruleset-details vop-bg-whisper" v-if="isVisible">
      <div>
        <!-- Toolbar -->
        <v-toolbar flat color="vop-bg-whisper">
          <!-- Title -->
          <v-toolbar-title class="subheading ttu font-weight-medium">
            <span>{{ messages.label.RULE_SET }} - {{ selectedRuleSet.title }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <!-- Test button -->
          <v-btn color="secondary" @click="showTestRuleModal()" round small>
            {{ messages.label.TEST }}
          </v-btn>
          <v-toolbar-items>
            <!-- Edit button -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon medium v-on="on" @click="goToEditView()" class="grey--text text--darken-2">
                  <v-icon>mdi-circle-edit-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit RuleSet</span>
            </v-tooltip>
            <!-- More Menu -->
            <v-menu offset-y left v-show="!inDialog">
              <v-tooltip bottom slot="activator">
                <template v-slot:activator="{ on }">
                  <v-btn icon medium v-on="on" class="grey--text text--darken-2">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <span>More</span>
              </v-tooltip>
              <v-list>
                <v-list-tile @click="showCopyRuleModal()">
                  <v-list-tile-title>{{ messages.label.COPY_RULE_SET }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="showOverrideRuleModal()">
                  <v-list-tile-title>{{ messages.label.OVERRIDE_RULE_SET }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile class="error--text" @click="showDeleteDialog()">
                  <v-list-tile-title>{{ messages.label.DELETE }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <!-- Close button -->
            <v-btn icon @click="hide()">
              <v-icon medium>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <!-- Main Content -->
        <v-card-text>
          <v-progress-linear :indeterminate="true" height="4" v-if="ruleSetLoading">
          </v-progress-linear>
          <!-- Error Alert -->
          <v-flex xs12 v-if="errorAlert">
            <v-alert v-model="errorAlert" dismissible type="error">
              {{ errorMessage }}
            </v-alert>
          </v-flex>
          <v-tabs v-model="activeTab" color="vop-bg-whisper" slider-color="primary">
            <v-tab v-for="item in tabItems" :key="item">
              {{ item }}
            </v-tab>
          </v-tabs>
          <v-layout wrap>
            <v-flex xs12>
              <v-tabs-items v-model="activeTab">
                <!-- Rule Set Details -->
                <v-tab-item>
                  <v-data-table :items="ruleSetDetails" hide-actions hide-headers class="mt-2">
                    <template slot="items" slot-scope="props">
                      <td class="text-xs-left ttu" width="30%">{{ props.item.name | ownerLabel }}</td>
                      <td class="text-xs-left">
                        <!-- Inline Title Edit -->
                        <template v-if="props.item.name === 'title'">
                          <div v-if="inDialog">
                            {{ props.item.value }}
                          </div>
                          <div v-else>
                            <v-edit-dialog :return-value.sync="props.item.value" large lazy persistent
                              @save="saveField(props.item.value, 'title')" @cancel="cancelInlineEdit">
                              <!-- Title Display -->
                              <div v-if="!$lodash.isEmpty(props.item.value)">
                                {{ props.item.value }}
                                <v-icon small class="ml-2" color="primary">mdi-circle-edit-outline</v-icon>
                              </div>
                              <div v-else>
                                {{ messages.label.ADD_TITLE }}
                                <v-icon small class="ml-2" color="primary">add_circle</v-icon>
                              </div>
                              <!-- Title Edit Input -->
                              <template v-slot:input>
                                <div class="mt-3 subtitle ttu" v-if="!$lodash.isEmpty(props.item.value)">
                                  {{ messages.label.UPDATE_TITLE }}
                                </div>
                                <div class="mt-3 subtitle ttu" v-else>{{ messages.label.ADD_TITLE }}
                                </div>
                                <v-text-field v-model="props.item.value" clearable v-validate="'required|max:100'"
                                  :error-messages="errors.collect('title')" data-vv-name="title" :counter="100"
                                  :label="messages.label.RULE_TITLE" single-line autofocus>
                                </v-text-field>
                              </template>
                            </v-edit-dialog>
                          </div>
                        </template>
                        <!-- Inline Description Edit -->
                        <template v-else-if="props.item.name === 'description'">
                          <div v-if="inDialog">
                            {{ props.item.value | displayNA }}
                          </div>
                          <div v-else>
                            <v-edit-dialog :return-value.sync="props.item.value" large lazy persistent
                              @save="saveField(props.item.value, 'description')" @cancel="cancelInlineEdit">
                              <!-- Description Display -->
                              <div v-if="!$lodash.isEmpty(props.item.value) && !$lodash.isNull(props.item.value)">
                                {{ props.item.value }}
                                <v-icon small class="ml-2" color="primary">mdi-circle-edit-outline</v-icon>
                              </div>
                              <div v-else>
                                {{ messages.label.ADD_DESCRIPTION }}
                                <v-icon small class="ml-2" color="primary">add_circle</v-icon>
                              </div>
                              <!-- Description Edit Input -->
                              <template v-slot:input>
                                <div class="mt-3 subtitle ttu" v-if="!$lodash.isEmpty(props.item.value)">
                                  {{ messages.label.UPDATE_DESCRIPTION }}
                                </div>
                                <div class="mt-3 subtitle ttu" v-else>{{ messages.label.ADD_DESCRIPTION }}
                                </div>
                                <v-textarea v-model="props.item.value" clearable v-validate="'max:500'"
                                  :error-messages="errors.collect('description')" data-vv-name="description"
                                  :counter="500" :label="messages.label.RULE_DESCRIPTION" single-line autofocus>
                                </v-textarea>
                              </template>
                            </v-edit-dialog>
                          </div>
                        </template>
                        <!-- Inline Enabled status edit -->
                        <template v-else-if="props.item.name === 'enabled'">
                          <div v-if="inDialog">
                            {{ props.item.value }}
                          </div>
                          <div v-else>
                            <v-edit-dialog :return-value.sync="props.item.value" large lazy persistent
                              @save="saveField(props.item.value, 'enabled')" @cancel="cancelInlineEdit">
                              <!-- Enabled Status Display -->
                              {{ props.item.value }}
                              <v-icon small class="ml-2" color="primary">mdi-circle-edit-outline</v-icon>
                              <!-- Description Edit Input -->
                              <template v-slot:input>
                                <div class="mt-3 subtitle ttu">
                                  {{ messages.label.UPDATE_ENABLED }}
                                </div>
                                <v-switch v-model="props.item.value" class="mt-0" hide-details color="primary"
                                  :label="`${(props.item.value).toString()}`">
                                </v-switch>
                              </template>
                            </v-edit-dialog>
                          </div>
                        </template>
                        <!-- Owner -->
                        <template v-else-if="props.item.name === 'ownerId'">
                          {{ getOwnerName(props.item.value) }}
                        </template>
                        <!-- Provider -->
                        <template v-else-if="props.item.name === 'secondaryOwner'">
                          {{ getProviderName(props.item.value) }}
                        </template>
                        <template v-else>
                          <div>
                            {{ props.item.value }}
                          </div>
                        </template>
                      </td>
                    </template>
                  </v-data-table>
                </v-tab-item>
                <!-- Rules -->
                <v-tab-item>
                  <v-data-table :headers="rulesListHeaders" :items="ruleSetRules" class="mt-1" ref="draggableTable">
                    <template slot="items" slot-scope="props">
                      <td class="handle" style="max-width: 28px" v-if="displayActionsCount">
                        <v-icon>drag_handle</v-icon>
                      </td>
                      <td class="text-xs-center" v-if="displayActionsCount">{{ props.item.sequence + 1 }}</td>
                      <td class="text-xs-left" width="30%">{{ props.item.title }}</td>
                      <td class="text-xs-left">{{ getProviderName(props.item.secondaryOwner) }}</td>
                      <td class="text-xs-center">{{ getRuleConditionsCount(props.item.conditions) }}</td>
                      <td class="text-xs-center" v-if="displayActionsCount">{{ getRuleActionsCount(props.item.actions)
                      }}</td>
                      <td class="text-xs-left">{{ props.item._enabled }}</td>
                      <td class="justify-center layout px-0">
                        <v-tooltip bottom slot="activator">
                          <template v-slot:activator="{ on }">
                            <v-icon small v-on="on" @click="openRuleQuickView(props.item)" class="mr-2">
                              open_in_new
                            </v-icon>
                          </template>
                          <span>View Details</span>
                        </v-tooltip>
                        <v-tooltip bottom slot="activator">
                          <template v-slot:activator="{ on }">
                            <v-icon small v-on="on" @click="goToEditRuleView(props.item)" class="mr-2">
                              mdi-circle-edit-outline
                            </v-icon>
                          </template>
                          <span>Edit Rule</span>
                        </v-tooltip>
                      </td>
                    </template>
                    <!-- No Data -->
                    <template slot="no-data">
                      <v-alert :value="true" color="info" class="black--text" icon="info">
                        {{ messages.info.NO_RULES }}
                      </v-alert>
                    </template>
                  </v-data-table>
                  <v-flex>
                    <v-btn color="primary" v-if="displayUpdateRulesSequenceButton" :disabled="updatingRuleSet"
                      :loading="updatingRuleSet" @click="updateRulesSequence()">
                      {{ messages.label.SAVE }}
                    </v-btn>
                  </v-flex>
                </v-tab-item>
                <v-tab-item>
                  <JSONView :json="selectedRuleSetJSON" />
                </v-tab-item>
              </v-tabs-items>
            </v-flex>
          </v-layout>
        </v-card-text>
      </div>
      <v-card-actions class="px-3">
        <v-spacer />
        <v-flex v-if="inDialog" class="text-lg-right">
          <v-btn color="primary" @click="hide()">
            {{ messages.label.OK }}
          </v-btn>
        </v-flex>
      </v-card-actions>
      <!-- Rule Test Dialog -->
      <MetadataRuleTest v-model="ruleTestDialog" :rule-obj="ruleObjectToTest" />
      <!-- Rule Copy Dialog -->
      <MetadataRuleCopy v-model="ruleCopyDialog" :rule-obj="ruleObjectToCopy" />
      <!-- Rule Override Dialog -->
      <MetadataRuleOverride v-model="ruleOverrideDialog" :rule-obj="ruleObjectToOverride" />
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="primary" @click.native="snackbar.show = false">Ok</v-btn>
        </v-snackbar>
      </v-flex>
      <!-- Dialogs -->
      <v-dialog v-model="ruleQuickViewDialog" width="1000" height="800">
        <RuleQuickView />
      </v-dialog>
      <!-- Delete Dialog -->
      <v-dialog v-model="deleteDialog" width="500">
        <v-card>
          <v-card-title class="subheading ttu">
            {{ messages.label.DELETE }}
          </v-card-title>
          <v-card-text>
            <!-- Error Alert -->
            <v-flex xs12 v-if="deleteErrorAlert">
              <v-alert v-model="deleteErrorAlert" type="error">
                {{ deleteErrorMessage }}
              </v-alert>
            </v-flex>
            <p>{{ messages.info.DELETE_RULE_1 }} <b>{{ selectedRuleSet.title }}</b> ? </p>
            <p v-html="messages.info.DELETE_RULE_2"></p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-1 white--text" @click="cancelDelete">
              {{ messages.label.CANCEL }}
            </v-btn>
            <v-btn color="error" :disabled="deletingRule" :loading="deletingRule" @click="deleteRule()">
              {{ messages.label.YES }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-slide-x-reverse-transition>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import * as utils from "@/Shared/utils"
import messages from "@/MetadataRules/messages.json"
import RuleQuickView from "@/MetadataRules/Rule/RuleQuickView"
import JSONView from "@/MetadataRules/JSONView"
import MetadataRuleTest from "@/MetadataRules/MetadataRuleTest"
import MetadataRuleCopy from "@/MetadataRules/MetadataRuleCopy"
import { IS_POLARIS, MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL } from "@/store/constants/getter-types"
import MetadataRuleOverride from "@/MetadataRules/MetadataRuleOverride"
import Sortable from "sortablejs"

export default {
  name: "RuleSetQuickView",
  $_veeValidate: {
    validator: "new"
  },
  components: {
    RuleQuickView,
    JSONView,
    MetadataRuleTest,
    MetadataRuleCopy,
    MetadataRuleOverride
  },
  data() {
    return {
      snackbar: {
        show: false,
        timeout: 3000,
        color: "error",
        message: ""
      },
      ruleObjectToTest: {},
      ruleObjectToCopy: {},
      ruleObjectToOverride: {},
      ruleTestDialog: false,
      ruleCopyDialog: false,
      ruleOverrideDialog: false,
      activeTab: null,
      tabItems: ["DETAILS", "RULES", "{JSON}"],
      selectedRuleSet: {},
      selectedRuleSetJSON: {},
      isVisible: false,
      ruleSetDetails: [],
      ruleSetRules: [],
      originalRuleSetRules: [],
      errorAlert: false,
      errorMessage: "",
      ruleSetLoading: false,
      inDialog: false,
      ruleQuickViewDialog: false,
      deleteDialog: false,
      deletingRule: false,
      deleteErrorAlert: false,
      deleteErrorMessage: "",
      updatingRuleSet: false,
      dictionary: this.getValidationDictionary()
    }
  },
  computed: {
    ...mapState([
      "mpxParentAccount", "providerGroups", "mpxSubAccounts"
    ]),
    ...mapGetters([
      IS_POLARIS, MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL
    ]),
    rulesListHeaders() {
      return this.getRulesListHeaders()
    },
    /**
     * displayActionsCount  - identify if actions count column needs to be displayed
     * @returns {boolean}
     */
    displayActionsCount() {
      let display = false
      if (!this.$lodash.isUndefined(this.selectedRuleSet) &&
        this.selectedRuleSet.type === "TRANSFORMATION") {
        display = true
        this.$nextTick(() => {
          let table = this.$refs.draggableTable.$el.getElementsByTagName("tbody")[0]
          const _self = this
          Sortable.create(table, {
            handle: ".handle", // Use handle so user can select text
            onEnd({ newIndex, oldIndex }) {
              const rowSelected = _self.ruleSetRules.splice(oldIndex, 1)[0] // Get the selected row and remove it
              _self.ruleSetRules.splice(newIndex, 0, rowSelected) // Move it to the new index
              _self.resetRulesSequenceNumbers()
              _self.sortRulesOnSequence()
            }
          })
        })
      }
      return display
    },
    /**
     * displayUpdateRulesSequenceButton  - identify if user has changed the sequence of rules within ruleset
     * @returns {boolean}
     */
    displayUpdateRulesSequenceButton() {
      let display = false
      let ruleSetRulesArray = []
      if (this.ruleSetRules.length > 0) {
        this.$lodash.forEach(this.ruleSetRules, (object, index) => {
          // Remove sequence field before comparing
          object = this.$lodash.omit(object, ["sequence"])
          ruleSetRulesArray[index] = object
        })
      }
      if (JSON.stringify(ruleSetRulesArray) !== JSON.stringify(this.originalRuleSetRules)) {
        display = true
      }
      return display
    }
  },
  methods: {
    /**
     * show - Show ruleset details view
     * @param {object} selectedRuleSet
     */
    show(selectedRuleSet) {
      this.resetData()
      this.selectedRuleSet = selectedRuleSet
      this.selectedRuleSetOriginal = this.$lodash.cloneDeep(this.selectedRuleSet)
      this.selectedRuleSetJSON = {}
      this.retrieveRuleSet()
      if (!this.isVisible) { // If already visible, leave tab selection as is
        this.isVisible = true
        this.activeTab = 0
      }
    },

    /**
     * showInDialog - Show quick view dialog
     * @param {object} selectedRuleSet
     */
    showInDialog(selectedRuleSet) {
      this.inDialog = true
      this.show(selectedRuleSet)
    },

    /**
     * resetData - Reset data
     */
    resetData() {
      this.errorAlert = false
      this.errorMessage = ""
    },

    /**
     * getValidationDictionary - Validation messages for vee-validate
     * @returns {object}
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 100 characters"
          },
          description: {
            max: "Description may not be greater than 100 characters"
          }
        }
      }
    },

    /**
    * getRulesListHeaders - Construct array of objects to show table headers
    * @return {array}
    */
    getRulesListHeaders() {
      let headerToDisplay = []
      if (this.displayActionsCount) {
        headerToDisplay = [{
          text: "",
          align: "left",
          sortable: false
        },
        {
          text: "SEQUENCE",
          align: "center",
          value: "sequence",
          sortable: true
        }]
      }
      let commonHeaders = [{
        text: messages.label.TH_TITLE,
        align: "left",
        value: "title",
        sortable: true
      },
      {
        text: messages.label.TH_PROVIDER,
        sortable: true,
        align: "left",
        value: "secondaryOwner"
      },
      {
        text: messages.label.TH_CONDITIONS_COUNT,
        sortable: false,
        align: "center",
        value: "conditions"
      }
      ]
      headerToDisplay = headerToDisplay.concat(commonHeaders)
      if (this.displayActionsCount) {
        headerToDisplay = headerToDisplay.concat([{
          text: messages.label.TH_ACTIONS_COUNT,
          sortable: false,
          align: "center",
          value: "actions"
        },
        {
          text: messages.label.TH_ENABLED,
          sortable: true,
          align: "left",
          value: "enabled"
        }])
      } else {
        headerToDisplay = headerToDisplay.concat([{
          text: messages.label.TH_ENABLED,
          sortable: true,
          align: "left",
          value: "enabled"
        }])
      }
      headerToDisplay = headerToDisplay.concat([{
        text: "",
        sortable: false
      }])
      return headerToDisplay
    },

    /**
     * retrieveRuleSet  - retrieve rule list details
     * This service call will retrieve all the rules details along with rules list details
     */
    retrieveRuleSet() {
      this.ruleSetLoading = true
      let queryObject = {
        ruleSetGuid: this.selectedRuleSet.guid,
        account: this.selectedRuleSet.ownerId,
        secondaryOwner: this.selectedRuleSet.secondaryOwner
      }
      metadataRulesDataService
        .getRuleSetDetails(queryObject,
          this.MVATS_WEB_SERVICE_URL)
        .then((response) => {
          // Add the client only fields to this object as well
          this.selectedRuleSetJSON = this.$lodash.cloneDeep(response.data)
          let tempArray = []
          tempArray.push(response.data)
          tempArray = MetadataRulesService.addClientOnlyFields(tempArray)
          this.selectedRuleSet = tempArray[0]
          this.constructRuleSetDetails()
          this.ruleSetRules = MetadataRulesService.addClientOnlyFields(this.selectedRuleSet.rules)
          this.originalRuleSetRules = this.$lodash.cloneDeep(this.ruleSetRules)
          this.resetRulesSequenceNumbers()
        })
        .catch((error) => {
          let isServerError = utils.isMPXError(error)
          let isClientError = utils.isClientError(error)
          if (isServerError) {
            this.errorAlert = true
            this.errorMessage = error.response.data.description
          } else if (isClientError) {
            this.errorAlert = true
            this.errorMessage = error.message
          }
        })
        .finally(() => {
          this.ruleSetLoading = false
        })
    },

    /**
     * constructRuleSetDetails - Construct selected rule details
     * This is being done to display in a data table
     */
    constructRuleSetDetails() {
      this.ruleSetDetails = []

      let requiredFields = ["title", "type", "description", "_enabled", "ownerId", "providerGroup", "secondaryOwner", "_added", "_updated"]
      requiredFields.forEach((fieldName) => {
        let obj = {}
        if (this.selectedRuleSet.hasOwnProperty(fieldName)) {
          obj["name"] = fieldName.charAt(0) === "_" ? fieldName.substring(1) : fieldName
          obj["value"] = this.selectedRuleSet[fieldName]
          this.ruleSetDetails.push(obj)
        } else if (fieldName === "providerGroup") {
          if (this.IS_POLARIS) {
            obj["name"] = fieldName
          } else {
            obj["name"] = "Sub Account"
          }
          obj["value"] = this.getProviderGroupName(this.selectedRuleSet.ownerId)
          this.ruleSetDetails.push(obj)
        }
      })
    },

    /**
     * saveTitle - Save changes made to title
     * @param {string} value
     */
    saveField(value, fieldName) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          let ruleSetObjToUpdate = this.$lodash.cloneDeep(this.selectedRuleSetOriginal)
          let valueChanged = false
          if (fieldName === "enabled") {
            ruleSetObjToUpdate.disabled = !value
            valueChanged = !(ruleSetObjToUpdate.disabled === this.selectedRuleSetOriginal.disabled)
          } else {
            value = this.$lodash.isNull(value) ? "" : value
            ruleSetObjToUpdate[fieldName] = value
            valueChanged = !(ruleSetObjToUpdate[fieldName] === this.selectedRuleSetOriginal[fieldName])
          }
          // Update only when the value has changed
          if (valueChanged) {
            ruleSetObjToUpdate = this.$lodash.omit(ruleSetObjToUpdate, ["_added", "_enabled", "_updated"])
            this.updateRuleSet(ruleSetObjToUpdate)
          }
        } else {
          // Clickingon 'clearable' icon sets the value to null
          let fieldObjIndex = this.$lodash.findIndex(this.ruleSetDetails, { "name": fieldName })
          // Revert to original value
          let originalObj = {
            name: fieldName,
            value: this.selectedRuleSetOriginal[fieldName]
          }
          // NOTE: This might seem tricky but, this code is trying to addresss the reactivity issues related to vue
          // when dealing with arrays. We are assigning a new array instead of modifying the array
          let ruleSetDetailsCopy = this.$lodash.cloneDeep(this.ruleSetDetails)
          this.ruleSetDetails = [] // to help with reactivity issue
          ruleSetDetailsCopy.splice(fieldObjIndex, 1, originalObj)
          this.$nextTick(() => {
            this.ruleSetDetails = ruleSetDetailsCopy
          })
        }
      })
    },

    /**
     *updateRulesSequence-Update Rules Sequence within ruleSet
     */
    updateRulesSequence() {
      this.updatingRuleSet = true
      let ruleSetObjToUpdate = this.$lodash.cloneDeep(this.selectedRuleSetOriginal)
      ruleSetObjToUpdate.items = this.getRuleSetRuleGUIDs()
      ruleSetObjToUpdate = this.$lodash.omit(ruleSetObjToUpdate, ["_added", "_enabled", "_updated"])
      this.updateRuleSet(ruleSetObjToUpdate)
    },

    /**
     * getRuleSetRuleIds - Retrieves just the rule GUIDs required for update in sequence
     */
    getRuleSetRuleGUIDs() {
      return this.$lodash.map(this.ruleSetRules, "guid")
    },

    /**
     * Cancels inline editing and resets the validation
     */
    cancelInlineEdit() {
      this.$validator.reset()
    },

    /**
     * updateRuleSet - update the rule set
     * @param {object} ruleSetObj
     */
    updateRuleSet(ruleSetObj) {
      this.updatingRuleSet = true
      metadataRulesDataService.updateRuleSet(ruleSetObj, ruleSetObj.ownerId, this.RULE_MGMT_DATA_SERVICE_URL)
        .then(() => {
          this.resetData()
          // Show snack bar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message = this.messages.info.RULE_SET_UPDATED
          MetadataRulesService.$emit("event:refreshRulesList")
        })
        .catch((error) => {
          let response = error.response.data
          if (response.isException) {
            this.errorAlert = true
            this.errorMessage = response.description
            // Re-populate original data
            this.retrieveRuleSet()
          }
        })
        .finally(() => {
          this.updatingRuleSet = false
        })
    },

    /**
     * getRuleConditionsCount - return count of conditions exists in a rule
     * @param {array} conditionsArray
     * @returns {number}
     */
    getRuleConditionsCount(conditionsArray) {
      if (!this.$lodash.isUndefined(conditionsArray)) {
        return conditionsArray.length
      } else {
        return 0
      }
    },

    /**
     * getRuleActionsCount - return count of actions exists in a rule
     * @param {array} actionsArray
     * @returns {number}
     */
    getRuleActionsCount(actionsArray) {
      if (!this.$lodash.isUndefined(actionsArray)) {
        return actionsArray.length
      } else {
        return 0
      }
    },

    /**
     * Reset / update sequence numbers for rules within rule Set
     */
    resetRulesSequenceNumbers() {
      if (this.ruleSetRules.length > 0) {
        this.$lodash.forEach(this.ruleSetRules, (rule, index) => {
          rule["sequence"] = index
        })
      }
    },

    /**
     * Sort rules within rule set based on sequence number
     */
    sortRulesOnSequence() {
      if (this.ruleSetRules.length > 0) {
        let sortedList = this.$lodash.sortBy(this.ruleSetRules, "sequence")
        this.ruleSetRules = []
        this.$nextTick(() => {
          this.ruleSetRules = sortedList
        })
      }
    },

    /**
     * hide - Hide the quick view
     */
    hide() {
      if (this.inDialog) {
        MetadataRulesService.$emit("event:hideRuleSetQuickViewDialog")
      } else {
        MetadataRulesService.$emit("event:hideRuleSetQuickView")
      }
      this.resetData()
      this.isVisible = false
    },

    /**
     * goToEditView - Navigate to Edit view
     */
    goToEditView() {
      let id = this.selectedRuleSet.id.split("/").pop()
      let ownerId = this.selectedRuleSet.ownerId
      this.$router.push({
        name: "ManageRuleSet",
        params: { id: id },
        query: { account: ownerId }
      })
    },

    /**
     * goToEditRuleView - Navigate to Edit view
     * @param {object} rule
     */
    goToEditRuleView(rule) {
      let id = rule.id.split("/").pop()
      let ownerId = rule.ownerId
      this.$router.push({ name: "ManageRule", params: { id: id }, query: { account: ownerId } })
    },

    /**
     * openRuleQuickView - Open rule quick view in dialog
     */
    openRuleQuickView(ruleObj) {
      this.ruleQuickViewDialog = true
      MetadataRulesService.$emit("event:showRuleQuickViewDialog", ruleObj)
    },

    /**
     * closeRuleQuickView - close the dialog view of rule quick view
     */
    closeRuleQuickView() {
      this.ruleQuickViewDialog = false
    },

    /**
     * showDeleteDialog -
     */
    showDeleteDialog() {
      this.deleteDialog = true
    },

    /**
     * cancelDelete - close delete dialog
     */
    cancelDelete() {
      this.deleteErrorAlert = false
      this.deleteErrorMessage = ""
      this.deleteDialog = false
    },

    /**
     * deleteRule - Delete selected rule / ruleset / rule collection
     */
    deleteRule() {
      this.deletingRule = true
      let ruleSetId = this.selectedRuleSet.id.split("/").pop()
      metadataRulesDataService
        .deleteRuleSet(ruleSetId,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then(() => {
          this.deleteDialog = false
          // Show Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message = this.messages.info.DELETE_SUCCESS + this.selectedRuleSet.title
          MetadataRulesService.$emit("event:refreshRulesList")
        }).catch((error) => {
          let isServerError = utils.isMPXError(error)
          if (isServerError) {
            this.deleteErrorAlert = true
            this.deleteErrorMessage = error.response.data.description
          }
        })
        .finally(() => {
          this.deletingRule = false
        })
    },

    /**
     * showTestRuleModal
     */
    showTestRuleModal() {
      this.ruleObjectToTest = this.selectedRuleSet
      this.ruleTestDialog = true
    },

    /**
     * showCopyRuleModal
     */
    showCopyRuleModal() {
      this.ruleObjectToCopy = this.selectedRuleSetOriginal
      this.ruleCopyDialog = true
    },

    /**
     * showOverrideRuleModal
     */
    showOverrideRuleModal() {
      this.ruleObjectToOverride = this.selectedRuleSetOriginal
      this.ruleOverrideDialog = true
    },

    /**
     * getOwnerName
     * @param {stirng} ownerId
     */
    getOwnerName(ownerId) {
      if (this.IS_POLARIS) {
        return MetadataRulesService.getOwnerName(ownerId, this.mpxParentAccount.id, this.providerGroups, true)
      } else {
        return MetadataRulesService.getOwnerName(ownerId, this.mpxParentAccount.id, this.mpxSubAccounts, false)
      }
    },

    /**
     * getOwnerName
     * @param {stirng} secondaryOwner
     */
    getProviderName(secondaryOwner) {
      return MetadataRulesService.getProviderName(secondaryOwner)
    },

    /**
     * getProviderGroupName
     * @param {stirng} ownerId
     */
    getProviderGroupName(ownerId) {
      if (this.IS_POLARIS) {
        return MetadataRulesService.getProviderGroupName(ownerId, this.mpxParentAccount.id, this.providerGroups, true)
      } else {
        return MetadataRulesService.getProviderGroupName(ownerId, this.mpxParentAccount.id, this.mpxSubAccounts, false)
      }
    }
  },
  filters: {
    ownerLabel(value) {
      return MetadataRulesService.getOwnerLabel(value)
    },
    displayNA(value) {
      if (value === null || value === "") {
        return "N/A"
      } else {
        return value
      }
    }
  },
  mounted() {
    // Validator initialization
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    MetadataRulesService.$on("event:showRuleSetQuickView", this.show)
    MetadataRulesService.$on("event:showRuleSetQuickViewDialog", this.showInDialog)
    MetadataRulesService.$on("event:hideRuleQuickViewDialog", this.closeRuleQuickView)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:showRuleSetQuickView", this.show)
    MetadataRulesService.$off("event:showRuleSetQuickViewDialog", this.showInDialog)
    MetadataRulesService.$off("event:hideRuleQuickViewDialog", this.closeRuleQuickView)
  }
}
</script>

<style lang="scss" scoped>
.ruleset-details {
  min-height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.handle {
  cursor: move !important;
  cursor: -webkit-grabbing !important;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/RuleSet/RuleSetQuickView.vue